import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useNotificationWebSocket from '../../hooks/useNotificationWebSocket';
import NotificationToast from './NotificationToast';

const NotificationManager = () => {
  const { user } = useAuth();
  const [activeNotifications, setActiveNotifications] = useState([]);
  const [showReconnecting, setShowReconnecting] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const lastConnectedState = useRef(true);
  
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearAll
  } = useNotificationWebSocket();
  
  // Only show reconnection notification after 2 seconds of being disconnected
  useEffect(() => {
    if (isConnected) {
      // Connected - hide notification immediately and clear any pending timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setShowReconnecting(false);
      lastConnectedState.current = true;
    } else if (lastConnectedState.current) {
      // Just disconnected - start 2 second delay before showing
      lastConnectedState.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        setShowReconnecting(true);
      }, 2000);
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isConnected]);

  // Handle new notifications from WebSocket
  useEffect(() => {
    if (notifications.length > 0) {
      const latestNotification = notifications[0];
      
      // Check if this notification is already in active notifications
      const isAlreadyActive = activeNotifications.some(
        notif => notif.id === latestNotification.id
      );
      
      if (!isAlreadyActive) {
        // Add new notification to active list
        setActiveNotifications(prev => [latestNotification, ...prev]);
      }
    }
  }, [notifications, activeNotifications]);

  // Remove notification from active list
  const removeNotification = (notificationId) => {
    setActiveNotifications(prev => 
      prev.filter(notif => notif.id !== notificationId)
    );
    
    // Mark as read in the notification system
    markAsRead(notificationId);
  };

  // Clear all active notifications
  const clearAllNotifications = () => {
    setActiveNotifications([]);
    clearAll();
  };

  // Limit to 3 notifications at a time
  const displayNotifications = activeNotifications.slice(0, 3);

  if (!user) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {displayNotifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
      
      {/* Connection status indicator - only show after delay to prevent flickering */}
      {showReconnecting && !isConnected && (
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-600">
            🔌 Reconnecting to notifications...
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
